import { Box, Input, Select, Textarea } from '@nerdwallet/currency';
import classNames from 'classnames';
import { usePathname } from 'next/navigation';
import { Fragment, useEffect, useRef } from 'react';
import BaseThirdPartyEmbed from '~/app/components/third-party-embed/base-third-party-embed';
import styles from '~/app/components/third-party-embed/third-party-embed.module.scss';
import { useMediaQuery } from '~/app/lib/use-media-query';
import { DynamicThirdPartyEmbedProps } from './types';
import { useDynamicThirdPartyEmbed } from './use-dynamic-third-party-embed';

const EMBED_TYPES = [
  { label: 'Select', value: '' },
  { label: 'Iframe', value: 'iframe' },
  { label: 'Script', value: 'script' },
  { label: 'Calculator', value: 'calculator' },
];

export const DynamicThirdPartyEmbed = ({
  embedType,
  iframeSrc,
  scriptSrc,
  cffId,
  height: desktopHeight,
  width: desktopWidth,
  tabletHeight,
  tabletWidth,
  mobileHeight,
  mobileWidth,
  customCss,
}: DynamicThirdPartyEmbedProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const pathname = usePathname();

  const size = useMediaQuery();
  const isTablet = size === 'md';
  const isMobile = size === 'sm';

  let height = desktopHeight;
  if (isTablet) height = tabletHeight;
  if (isMobile) height = mobileHeight;

  let width = desktopWidth;
  if (isTablet) width = tabletWidth;
  if (isMobile) width = mobileWidth;

  const isCalculatorEmbedType = embedType === 'calculator';

  useEffect(() => {
    const iframe = contentRef.current?.querySelector('iframe');

    intervalRef.current = setInterval(() => {
      iframeRef.current = contentRef?.current?.querySelector('iframe') || null;
      try {
        if (iframeRef.current && iframeRef.current?.contentDocument) {
          iframeRef.current.contentDocument.head.innerHTML += `<style id="injected-style" >${
            customCss || ''
          }</style>`;
          const cssInjector =
            iframe?.contentDocument?.getElementById('injected-style');
          if (cssInjector) {
            clearInterval(intervalRef.current!);
          }
        }
      } catch (error) {
        console.info('Iframe element not found!');
      }
    }, 500);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [customCss]);

  return (
    <div
      className={classNames([
        styles.card,
        {
          [styles.calculatorFixWidth]: isCalculatorEmbedType,
        },
      ])}
      ref={contentRef}
      id="content-wrapper"
    >
      <BaseThirdPartyEmbed
        embedType={embedType || 'iframe'}
        iframeSrc={iframeSrc || ''}
        scriptSrc={scriptSrc || ''}
        cffId={cffId || ''}
        height={`${height}px` || ''}
        width={`${width}px` || ''}
        pathname={pathname}
      />
    </div>
  );
};

export const DynamicThirdPartyEmbedForm = ({
  fieldPath = '',
}: {
  fieldPath?: string;
}) => {
  const {
    embedType,
    iframeSrc,
    scriptSrc,
    cffId,
    height,
    width,
    tabletHeight,
    tabletWidth,
    mobileHeight,
    mobileWidth,
    customCss,
    handleEmbedTypeChange,
    handleIframeSrcChange,
    handleScriptSrcChange,
    handleCffIdChange,
    handleHeightChange,
    handleWidthChange,
    handleTabletHeightChange,
    handleTabletWidthChange,
    handleMobileHeightChange,
    handleMobileWidthChange,
    handleCustomCssChange,
  } = useDynamicThirdPartyEmbed({
    fieldPath,
  });

  return (
    <Box paddingY={2}>
      <Box paddingY={2}>
        <Select
          label="Embed Type"
          value={embedType}
          onChange={handleEmbedTypeChange}
          options={EMBED_TYPES}
          fullWidth
        />
      </Box>
      {embedType === 'iframe' && (
        <Box paddingY={2}>
          <Input
            label="Iframe Source"
            value={iframeSrc}
            onChange={handleIframeSrcChange}
            fullWidth
          />
        </Box>
      )}

      {embedType === 'script' && (
        <Box paddingY={2}>
          <Input
            label="Script Source"
            value={scriptSrc}
            onChange={handleScriptSrcChange}
            fullWidth
            tooltip="Script embeds will only show up outside builder page."
          />
        </Box>
      )}

      {embedType === 'calculator' && (
        <Box paddingY={2}>
          <Input
            label="CFF ID"
            value={cffId}
            onChange={handleCffIdChange}
            fullWidth
          />
        </Box>
      )}

      {embedType === 'iframe' && (
        <Fragment>
          <Box paddingY={2}>
            <Input
              label="Height"
              value={height}
              onChange={handleHeightChange}
              fullWidth
            />
          </Box>

          <Box paddingY={2}>
            <Input
              label="Tablet Height"
              value={tabletHeight}
              onChange={handleTabletHeightChange}
              fullWidth
            />
          </Box>
          <Box paddingY={2}>
            <Input
              label="Mobile Height"
              value={mobileHeight}
              onChange={handleMobileHeightChange}
              fullWidth
            />
          </Box>
          <Box paddingY={2}>
            <Input
              label="Width"
              value={width}
              onChange={handleWidthChange}
              fullWidth
            />
          </Box>
          <Box paddingY={2}>
            <Input
              label="Tablet Width"
              value={tabletWidth}
              onChange={handleTabletWidthChange}
              fullWidth
            />
          </Box>
          <Box paddingY={2}>
            <Input
              label="Mobile Width"
              value={mobileWidth}
              onChange={handleMobileWidthChange}
              fullWidth
            />
          </Box>
        </Fragment>
      )}
      <Box paddingY={2}>
        <Textarea
          label="Custom CSS"
          value={customCss}
          onChange={handleCustomCssChange}
          fullWidth
        />
      </Box>
    </Box>
  );
};
